import { TransactionTypeEnumLegacy } from '@constants/enums/transaction/type';

import i18n from '@plugins/i18n';

import { Collection, Transaction, Investor, Account } from '@/models';

export class TransactionCollection extends Collection {
    static type = 'TransactionCollection';
    static endpoint = Transaction.api.index;
    static model = Transaction;

    static fields() {
        return {
            ...super.fields(),
            has_custom_filter: this.boolean(false),

            investor_id: this.string(null).nullable(),
            investor: this.belongsTo(Investor, 'investor_id'),

            account_id: this.string(null).nullable(),
            account: this.belongsTo(Account, 'account_id'),

            on_platform: this.boolean(null).nullable()
        };
    }

    get headers() {
        return [
            this.createHeader('pending_allotment', i18n.t('pending_allotment'), {
                filterable: {
                    operators: this.getOperators(['eq'])
                },
                format: 'boolean',
                active: false
            }),
            this.createHeader('tax_year', i18n.t('tax_year'), {
                active: false,
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/TaxYear')
                }
            }),
            this.createHeader('gi_ref', i18n.t('gi_ref'), {
                classList: ['no-wrap'],
                filterable: true,
                width: '12rem'
            }),
            this.createHeader('account_name', i18n.t('account'), {
                path: 'account.name',
                filterable: !this.account_id,
                width: '14rem'
            }),
            this.createHeader('fund_manager_name', i18n.t('fund_manager'), {
                path: 'fund_investment.fund.fund_manager.name',
                filterable: true,
                width: '18rem'
            }),
            this.createHeader('on_platform', i18n.t('on_platform'), {
                filterable: !this.account_id,
                format: 'boolean',
                width: '9rem'
            }),
            this.createHeader('in_custody', i18n.t('custodied'), {
                filterable: true,
                format: 'boolean',
                width: '9rem'
            }),
            this.createHeader('application_at', i18n.t('application_date'), {
                format: 'datetime',
                width: '12rem'
            }),
            this.createHeader('record_at', i18n.t('record_at'), {
                filterable: true,
                format: 'datetime',
                width: '12rem'
            }),
            this.createHeader('transaction_date', i18n.t('transaction_date'), {
                filterable: true,
                format: 'date',
                width: '11rem'
            }),
            this.createHeader('value_at', i18n.t('value_at'), {
                filterable: true,
                format: 'datetime',
                width: '8rem',
                formatOptions: {
                    showTime: true
                }
            }),
            this.createHeader('type', i18n.t('type'), {
                filterable: true,
                width: '8rem',
                format: 'enum',
                enum: TransactionTypeEnumLegacy,
                enumA11yKey: 'enums.transaction_type'
            }),
            this.createHeader('description', i18n.t('description'), {
                filterable: true,
                width: '20rem'
            }),
            this.createHeader('share_quantity', i18n.t('share_quantity'), {
                filterable: true,
                format: 'number',
                width: '8rem',
                fallback: data => {
                    if (data.item.pending_allotment) {
                        return i18n.t('pending');
                    }

                    return '-';
                }
            }),
            this.createHeader('share_price', i18n.t('share_price'), {
                filterable: true,
                format: 'money',
                formatOptions: super.money_format_options,
                width: '12rem',
                fallback: data => {
                    if (data.item.pending_allotment) {
                        return i18n.t('pending');
                    }

                    return '-';
                }
            }),
            this.createHeader('amount', i18n.t('amount'), {
                filterable: true,
                format: 'money',
                width: '10rem',
                formatOptions: {
                    decimals: 2,
                    abbreviate: false
                },
                classList: data => {
                    let classList = [];

                    if ('amount' in data.item && 'amount' in data.item.amount) {
                        if (data.item.amount.amount < 0) {
                            classList.push('error--text');
                            classList.push('text--darken-1');
                        }
                    }

                    return classList.join(' ');
                }
            }),
            this.createHeader('running_balance', i18n.t('account_balance'), {
                active: !!this.account_id,
                filterable: false,
                sortable: false,
                disabled: this.has_custom_filter || this.on_platform === false,
                tooltip:
                    this.has_custom_filter || this.on_platform === false
                        ? i18n.t('account_balance_cannot_be_determined_based_on_the_filters')
                        : null,
                format: 'money',
                width: '10rem',
                formatOptions: {
                    decimals: 2,
                    abbreviate: false
                }
            })
        ];
    }

    get default_headers() {
        return [
            'gi_ref',
            'account_name',
            'on_platform',
            'transaction_date',
            'value_at',
            'type',
            'description',
            'share_quantity',
            'share_issue_price',
            'amount',
            'running_balance'
        ];
    }

    get preset_filters() {
        let filters = {};

        if (this.off_platform) {
            filters.off_platform = true;
        }

        if (this.account_id) {
            filters.account_id = this.filterOperator('is') + ':' + this.account_id;
        } else if (this.investor_id) {
            filters.investor_id = this.filterOperator('is') + ':' + this.investor_id;
        }

        if (this.tax_year) {
            filters.tax_year = this.tax_year;
        }

        if (this.on_platform === false) {
            filters.on_platform = false;
        }

        return filters;
    }

    transformFilters(filters) {
        const presetFilterKeys = Object.keys(this.preset_filters);
        this.has_custom_filter = Object.keys(filters).some(key => !presetFilterKeys.includes(key));

        if (filters.account_id && !this.has_custom_filter && this.on_platform !== false) {
            // Will limit the response to only include transactions on platform
            filters.running_balance = true;
        }

        return filters;
    }
}

export default TransactionCollection;
